import { useCallback, useMemo, useState } from 'react';
import { InfiniteScrollProps } from '../../../../ui/components/pure/list-table-wrappers/infinite-table.component';
import { useQueryGetProject, useQueryGetProjectChildProjects } from '../../../../core/queries/projects.queries';
import useSearchParams, { UseSearchParamsOptions } from '../../../../hooks/use-search-params.hook';
import { UseQueryListOrdersParams, useInfiniteQueryOrdersList } from '../../orders.queries';
import { OrdersListActions } from '../actions/orders-list.actions';
import { KnownSortOrder } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useTimelineUpdatesEventCentre } from '../../../../events/timelineUpdates/useTimelineUpdatesEventCentre.hook';
import {
  TimelineUpdatesSubscriptionMatcher,
  TimelineUpdatesSubscriptionCallback,
  TimelineUpdatesSubscriptionStream
} from '../../../../events/timelineUpdates/timelineUpdates.types';
import { useQueryHubSummary } from '../../../lockers/hubs.queries';
import { useHubFilter } from '../../../lockers/hooks/use-hub-filter.hook';
import { getBookingFlowType } from '../../../../core/util/booking-flow-type.util';
import {
  liveRefreshIntervalForProjectType,
  useThrottledFirstPageRefetch
} from '../../../../hooks/use-throttled-first-page-refetch.hook';

const initialControlsState: UseQueryListOrdersParams = {
  status: '',
  searchTerm: '',
  fromDate: null,
  toDate: null,
  hubID: null,
  sortBy: 'bookingTime',
  sortOrder: KnownSortOrder.Desc
};

const searchParamOptions: UseSearchParamsOptions<UseQueryListOrdersParams> = {
  parse: {
    excludedValues: [''],
    convertBooleans: true,
    parseDates: ['fromDate', 'toDate']
  },
  stringify: {
    excludedValues: [null, ''],
    stringifyDates: true
  },
  config: {
    debouncedValues: ['searchTerm']
  }
};

export function useOrdersList(projectID: string) {
  const [currentAction, setCurrentAction] = useState<OrdersListActions | null>(null);
  const project = useQueryGetProject(projectID);
  const childProjects = useQueryGetProjectChildProjects(projectID);
  const [formData, queryData, setFormData] = useSearchParams(initialControlsState, searchParamOptions);
  const orders = useInfiniteQueryOrdersList(projectID, 1, queryData);
  const hubSummary = useQueryHubSummary(projectID, { includeChildProjects: true });
  const clearHub = useCallback(() => setFormData({ hubID: null }), [setFormData]);
  const hubFilter = useHubFilter(projectID, formData.hubID, clearHub);

  const projectIDsInScope = useMemo(() => (childProjects.data || []).map((p) => p.id), [childProjects.data]);

  // Subscribe to timeline update events
  const refetchFirstPage = useThrottledFirstPageRefetch(
    orders.refetch,
    liveRefreshIntervalForProjectType(project.data?.projectType)
  );
  const callback: TimelineUpdatesSubscriptionCallback = useCallback(() => {
    refetchFirstPage();
  }, [refetchFirstPage]);

  const matcher: TimelineUpdatesSubscriptionMatcher = useCallback(
    (event) => {
      if (event.data.projectID && projectIDsInScope.includes(event.data.projectID)) {
        return true;
      }
      return false;
    },
    [projectIDsInScope]
  );

  const stream: TimelineUpdatesSubscriptionStream = useMemo(
    () => ({ deliveryIDs: [], workflowIDs: [], projectIDs: projectIDsInScope }),
    [projectIDsInScope]
  );

  useTimelineUpdatesEventCentre({ callback, matcher, stream });

  function setAction(action: OrdersListActions | null) {
    setCurrentAction(action);
  }

  const showHubNameInTable = (hubSummary.data?.count ?? 0) > 1;

  const infiniteScroll: InfiniteScrollProps = {
    hasNextPage: orders.hasNextPage || false,
    isFetchingNextPage: orders.isFetchingNextPage,
    fetchNextPage: orders.fetchNextPage,
    onScrollToBottom() {
      orders.fetchNextPage();
    }
  };

  const bookingFlowType = getBookingFlowType(project.data);

  return {
    project,
    orders,
    infiniteScroll,
    formData,
    queryData,
    setFormData,
    actions: { currentAction, setAction },
    hubFilter,
    showHubNameInTable,
    bookingFlowType
  } as const;
}
